import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ArrowRight, ShieldCheck, Search } from "lucide-react";

const SLUG_TO_GENDER = { women: "female", men: "male" };
const SUB_FILTERS = ["ALL", "MODELS", "ACTORS", "ARTISTS", "FRESH FACES"];

export default function TalentDirectoryPage({ slug, title, subtitle }) {
  const gender = SLUG_TO_GENDER[slug] || null;
  const [items, setItems] = useState([]);
  const [sub, setSub] = useState("ALL");
  const [q, setQ] = useState("");

  useEffect(() => {
    const p = new URLSearchParams({ limit: "60", sort: slug === "new-faces" ? "recent" : "featured" });
    if (gender) p.set("gender", gender);
    if (slug === "models") p.set("category", "Model");
    if (slug === "actors") p.set("category", "Actor");
    if (slug === "creators") p.set("category", "Creator");
    api.get(`/talents?${p.toString()}`).then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, [slug, gender]);

  const filtered = useMemo(() => items.filter((t) => {
    const c = (t.data?.categories?.[0] || "").toLowerCase();
    if (sub === "MODELS" && !c.includes("model")) return false;
    if (sub === "ACTORS" && !c.includes("act")) return false;
    if (sub === "ARTISTS" && !c.match(/artist|singer|dancer|creator/)) return false;
    if (sub === "FRESH FACES" && !c.match(/fresh|new/) && !t.data?.new_face) return false;
    if (q) {
      const needle = q.toLowerCase();
      if (!(`${t.full_name} ${t.data?.city || ""} ${c}`.toLowerCase().includes(needle))) return false;
    }
    return true;
  }), [items, sub, q]);

  const linkFor = (t) => {
    const bucket = slug === "women" ? "women" : slug === "men" ? "men" : (t.data?.gender === "female" ? "women" : t.data?.gender === "male" ? "men" : "talent");
    return t.data?.slug ? `/${bucket}/${t.data.slug}` : `/talent/${t.user_id}`;
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-6">
        <div className="sv-overline mb-4">SV Talent Today · {title}</div>
        <h1 className="font-serif-hero text-5xl lg:text-8xl text-[#F4EFE4] uppercase leading-none">{title}</h1>
        <p className="mt-6 text-[#CDC7BA] max-w-2xl">{subtitle}</p>
      </section>
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-4">
        <div className="flex gap-3 flex-wrap py-4 border-b border-[rgba(255,255,255,0.06)] justify-between items-center">
          <div className="flex gap-2 flex-wrap">
            {SUB_FILTERS.map((f) => (
              <button key={f} onClick={() => setSub(f)} data-testid={`sub-${f}`}
                className={`px-4 py-2 rounded-full text-[10px] font-mono-label tracking-[0.32em] transition ${sub === f ? "bg-[#C5A059] text-[#0A0A0B]" : "text-[#CDC7BA] hover:text-[#F4E4BC] border border-[rgba(255,255,255,0.08)]"}`}>{f}</button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[#8A8478]" />
            <input placeholder="Search name / city / category" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`dir-search-${slug}`}
              className="pl-9 pr-4 py-2 bg-[#131316] border border-[rgba(197,160,89,0.2)] rounded-full text-xs text-[#F4EFE4] outline-none focus:border-[#C5A059] w-64" />
          </div>
        </div>
      </section>
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-10 pb-24">
        {filtered.length === 0 ? (
          <div className="sv-card p-12 text-center">
            <div className="sv-overline mb-3">No profiles yet</div>
            <p className="text-sm text-[#CDC7BA]">Approved talent will appear here.</p>
            <Link to="/membership" className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-[#C5A059] text-[#0A0A0B] font-mono-label text-[11px] tracking-[0.32em]">JOIN SV TALENT TODAY</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 lg:gap-6">
            {filtered.map((t) => (
              <Link key={t.user_id} to={linkFor(t)} data-testid={`talent-card-${t.data?.slug || t.user_id}`} className="talent-card sv-card overflow-hidden group">
                <div className="aspect-[3/4] overflow-hidden bg-[#131316]">
                  {t.data?.profile_photo
                    ? <img src={t.data.profile_photo} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={t.full_name} loading="lazy" />
                    : <div className="w-full h-full grid place-items-center text-[#4A4842] font-mono-label text-[10px]">No Image</div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-serif-editorial text-lg text-[#F4EFE4] truncate">{t.full_name?.toUpperCase()}</div>
                    {t.featured && <ShieldCheck size={13} className="text-[#C5A059]" />}
                  </div>
                  <div className="text-[11px] text-[#8A8478] mt-1">{t.data?.categories?.[0]}</div>
                  <div className="text-[11px] text-[#CDC7BA] mt-2 flex gap-3">
                    {t.data?.city && <span>{t.data.city}</span>}
                    {t.data?.height && <span className="text-[#8A8478]">·  {t.data.height}</span>}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-[10px] font-mono-label text-[#C5A059] tracking-[0.32em]">
                    VIEW PROFILE <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
